import { Button, Input, Segmented, Table, Tag, message } from 'antd';
import { motion } from 'framer-motion';
import { ArrowUpToLine, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import DashboardHero from '../Dashboard/DashboardHero';
import {
  type WorkLibraryItem,
  type WorkLibraryKind,
} from '../../services/epcWorkLibraryService';
import {
  useDeactivateLibraryItem,
  useUpdateLibraryItem,
  useWorkLibrary,
} from '../../features/epc/manager/hooks/useWorkLibrary';
import { LibraryItemEditorPopover } from '../../features/epc/manager/components/planbuilder/LibraryItemEditorPopover';
import { ResourceItemEditorModal } from '../../features/epc/manager/components/library/ResourceItemEditorModal';
import type { SkillLevel } from '../../features/epc/manager/types';

export default function WorkLibraryAdminPage() {
  const [kind, setKind] = useState<WorkLibraryKind>('safety');
  const [search, setSearch] = useState('');
  // Admin lists across all disciplines incl. inactive (disciplineKey omitted => all).
  const { data: items = [], isLoading } = useWorkLibrary(kind, null, { includeInactive: true });
  const deactivate = useDeactivateLibraryItem();
  const updateItem = useUpdateLibraryItem();
  const [editor, setEditor] = useState<WorkLibraryItem | null>(null);
  const [creating, setCreating] = useState(false);
  const isResourceKind = kind === 'manpower' || kind === 'machine';

  const filtered = useMemo(
    () => items.filter((i) => i.label.toLowerCase().includes(search.trim().toLowerCase())),
    [items, search],
  );

  const stats = useMemo(() => {
    const active = items.filter((i) => i.active);
    const disciplines = new Set(items.map((i) => i.disciplineLabel ?? 'Common'));
    return { active: active.length, disciplines: disciplines.size, inactive: items.length - active.length };
  }, [items]);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <DashboardHero
        title="Work Library"
        subtitle="Education, Safety, QA/QC gate templates & manpower/machine resource masters"
        variant="head"
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
      >
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active items</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{stats.active}</div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Disciplines</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{stats.disciplines}</div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inactive</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{stats.inactive}</div>
        </div>
      </motion.div>

      <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmented<WorkLibraryKind>
            value={kind}
            onChange={(v) => setKind(v)}
            options={[
              { value: 'education', label: 'Education' },
              { value: 'safety', label: 'Safety' },
              { value: 'qaqc', label: 'QA/QC' },
              { value: 'manpower', label: 'Manpower' },
              { value: 'machine', label: 'Machines' },
            ]}
          />
          <div className="flex items-center gap-2">
            <Input.Search
              allowClear
              placeholder="Search label…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              Add item
            </Button>
          </div>
        </div>

        <Table<WorkLibraryItem>
          rowKey="id"
          loading={isLoading}
          dataSource={filtered}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          columns={[
            { title: 'Label', dataIndex: 'label', sorter: (a, b) => a.label.localeCompare(b.label) },
            {
              title: 'Discipline',
              dataIndex: 'disciplineLabel',
              render: (v: string | null) => (v ? <Tag>{v}</Tag> : <Tag color="green">Common</Tag>),
            },
            {
              title: 'Visibility',
              dataIndex: 'visibility',
              render: (v: string, row) =>
                v === 'private' ? (
                  <Tag color="gold">
                    Private{row.createdBy?.name ? ` · ${row.createdBy.name}` : ''}
                  </Tag>
                ) : (
                  <Tag color="green">Org</Tag>
                ),
            },
            ...(kind === 'manpower'
              ? [
                  {
                    title: 'Skill',
                    key: 'skill',
                    render: (_v: unknown, row: WorkLibraryItem) =>
                      (row.payload as { skillLevel?: string } | undefined)?.skillLevel ?? '—',
                  },
                ]
              : []),
            ...(isResourceKind
              ? []
              : [
                  {
                    title: 'Work instruction',
                    dataIndex: 'workInstruction' as const,
                    render: (v: string) => (
                      <span className="line-clamp-1 text-slate-500">{v || '—'}</span>
                    ),
                  },
                ]),
            {
              title: 'Status',
              dataIndex: 'active',
              render: (active: boolean) =>
                active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
            },
            {
              title: '',
              key: 'actions',
              width: 120,
              render: (_v, row) => (
                <div className="flex items-center gap-2">
                  <button type="button" title="Edit" onClick={() => setEditor(row)}>
                    <Pencil size={15} className="text-slate-500 hover:text-slate-800" />
                  </button>
                  {row.visibility === 'private' && (
                    <button
                      type="button"
                      title="Promote to org"
                      onClick={() =>
                        updateItem.mutate(
                          { id: row.id, input: { visibility: 'org' } },
                          { onSuccess: () => message.success('Published to the whole organisation') },
                        )
                      }
                    >
                      <ArrowUpToLine size={15} className="text-slate-500 hover:text-emerald-700" />
                    </button>
                  )}
                  {row.active ? (
                    <button
                      type="button"
                      title="Deactivate"
                      onClick={() =>
                        deactivate.mutate(row.id, { onSuccess: () => message.success('Deactivated') })
                      }
                    >
                      <Trash2 size={15} className="text-slate-500 hover:text-red-600" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      title="Reactivate"
                      onClick={() =>
                        updateItem.mutate(
                          { id: row.id, input: { active: true } },
                          { onSuccess: () => message.success('Reactivated') },
                        )
                      }
                    >
                      <RotateCcw size={15} className="text-slate-500 hover:text-emerald-600" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      {editor &&
        (editor.kind === 'manpower' || editor.kind === 'machine' ? (
          <ResourceItemEditorModal
            kind={editor.kind}
            mode="edit"
            initial={{
              id: editor.id,
              label: editor.label,
              ...(editor.kind === 'manpower'
                ? {
                    skillLevel:
                      ((editor.payload as { skillLevel?: SkillLevel } | undefined)?.skillLevel ??
                        'unskilled'),
                  }
                : {}),
            }}
            onClose={() => setEditor(null)}
          />
        ) : (
          <LibraryItemEditorPopover
            open
            adminMode
            mode="edit"
            kind={editor.kind}
            disciplineKey={editor.disciplineKey}
            disciplineLabel={editor.disciplineLabel}
            initial={{ id: editor.id, label: editor.label, workInstruction: editor.workInstruction }}
            onClose={() => setEditor(null)}
            onResolved={() => setEditor(null)}
          />
        ))}
      {creating &&
        (kind === 'manpower' || kind === 'machine' ? (
          <ResourceItemEditorModal kind={kind} mode="create" onClose={() => setCreating(false)} />
        ) : (
          <LibraryItemEditorPopover
            open
            adminMode
            mode="create"
            kind={kind}
            disciplineKey={null}
            disciplineLabel={null}
            onClose={() => setCreating(false)}
            onResolved={() => setCreating(false)}
          />
        ))}
    </div>
  );
}
